export type MomentumLabel = "Progressing" | "Watch" | "Stalling" | "Stuck";

export type MomentumDirection = "Improving" | "Steady" | "Declining";

type MomentumLabelKey = "progressing" | "watch" | "stalling" | "stuck" ;
type MomentumDirectionKey = "improving" | "steady" | "declining";

const LABEL_DISPLAY: Record<MomentumLabelKey, MomentumLabel> = {
  progressing: "Progressing",
  watch: "Watch",
  stalling: "Stalling",
  stuck: "Stuck",
};

const DIRECTION_DISPLAY: Record<MomentumDirectionKey, MomentumDirection> = {
  improving: "Improving",
  steady: "Steady",
  declining: "Declining",
};

const DIRECTION_ARROW: Record<MomentumDirectionKey, string> = {
  improving: "↑",
  steady: "→",
  declining: "↓",
};

const BADGE_BASE = "transition-colors";

const LABEL_BADGE_CLASS: Record<MomentumLabelKey, string> = {
  progressing: `bg-success/10 text-success border-success/20 hover:bg-success/20 ${BADGE_BASE}`,
  watch: `bg-warning/10 text-warning border-warning/20 hover:bg-warning/20 ${BADGE_BASE}`,
  stalling: `bg-danger-secondary/10 text-danger-secondary border-danger-secondary/20 hover:bg-danger-secondary/20 ${BADGE_BASE}`,
  stuck: `bg-danger/10 text-danger border-danger/20 hover:bg-danger/20 ${BADGE_BASE}`,
};

/** Solid fill — timeline / clickable actions (reads as a real button). */
const LABEL_BUTTON_CLASS: Record<MomentumLabelKey, string> = {
  progressing: `bg-success text-white border-success hover:bg-success/90 ${BADGE_BASE}`,
  watch: `bg-warning text-white border-warning hover:bg-warning/90 ${BADGE_BASE}`,
  stalling: `bg-danger-secondary text-white border-danger-secondary hover:bg-danger-secondary/90 ${BADGE_BASE}`,
  stuck: `bg-danger text-white border-danger hover:bg-danger/90 ${BADGE_BASE}`,
};

function normalizeToken(value: string): string {
  return value
    .toLowerCase()
    .replace(/[,_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function toLabelKey(value: string | null | undefined): MomentumLabelKey | null {
  if (!value?.trim()) return null;
  const n = normalizeToken(value);
  if (n in LABEL_DISPLAY) return n as MomentumLabelKey;
  return null;
}

/** Lowercase momentum state for analytics (progressing / watch / stalling / stuck / new). */
export function momentumStateKey(value: string | null | undefined): MomentumLabelKey | null {
  return toLabelKey(value);
}

function toDirectionKey(value: string | null | undefined): MomentumDirectionKey | null {
  if (!value?.trim()) return null;
  const n = normalizeToken(value);
  if (n.includes("improv") || n.includes("fewer")) return "improving";
  if (n.includes("steady") || n.includes("same")) return "steady";
  if (n.includes("declin") || n.includes("more indicator")) return "declining";
  if (n in DIRECTION_DISPLAY) return n as MomentumDirectionKey;
  return null;
}

export function normalizeMomentumLabel(value: string | null | undefined): MomentumLabel | null {
  const key = toLabelKey(value);
  return key ? LABEL_DISPLAY[key] : null;
}

export function normalizeMomentumDirection(
  value: string | null | undefined,
): MomentumDirection | null {
  const key = toDirectionKey(value);
  return key ? DIRECTION_DISPLAY[key] : null;
}

export function momentumDirectionArrow(
  direction: string | MomentumDirection | null | undefined,
): string | null {
  const key = toDirectionKey(direction);
  return key ? DIRECTION_ARROW[key] : null;
}

export function momentumBadgeClass(label: string | null | undefined): string {
  const key = toLabelKey(label);
  return key ? LABEL_BADGE_CLASS[key] : "";
}

export function momentumButtonClass(label: string | null | undefined): string {
  const key = toLabelKey(label);
  return key ? LABEL_BUTTON_CLASS[key] : "";
}

export function formatMomentumDisplay(
  label: string | null | undefined,
  direction: string | null | undefined,
): { text: string; label: MomentumLabel; direction: MomentumDirection | null } | null {
  const labelKey = toLabelKey(label);
  if (!labelKey) return null;

  const displayLabel = LABEL_DISPLAY[labelKey];
  const directionKey = toDirectionKey(direction);
  const directionDisplay = directionKey ? DIRECTION_DISPLAY[directionKey] : null;
  const arrow = directionKey ? DIRECTION_ARROW[directionKey] : null;
  const text = arrow ? `${displayLabel} | ${directionDisplay} ${arrow}` : displayLabel;

  return {
    text,
    label: displayLabel,
    direction: directionKey ? DIRECTION_DISPLAY[directionKey] : null,
  };
}
